// src/pages/RecordsPage.tsx
//
// ★ TASK 13 — Annex A stories 9 and 10: "what happened to the bills/records I
// sent?" Every row is `GET /api/records` (Task 11), scope-filtered in SQL by
// `services/transfer/src/inbound-file-repository.ts`'s two predicates — the
// file predicate decides which DELIVERIES this session may see at all; the
// bill predicate is a SEPARATE gate on the money, so a record whose bill is
// out of bill-scope renders its outcome with NULL money rather than another
// party's figures (never collapsed into one predicate — see that file's own
// header).
//
// ⚠ Sorting is SERVER-SIDE, over the caller's WHOLE scope, exactly like
// BillsListPage/AuditPage: `RECORD_SORT_COLUMNS` in the repository is the
// whitelist, and every column here that claims to sort really does re-query
// the server, never a client-side reshuffle of one page.
//
// ★★ WAVE 9 — EXPORT IS BUILT, BOTH HALVES. This paragraph read: *"printable via
// the browser's own print function … P2C1 §12.52's FIRST half. PDF/Excel DOWNLOAD
// is NOT built here: it is Phase 5 Task 14's shared export kernel, and this page
// says so rather than implying a download exists."* The kernel landed in Task 14
// and thirteen administration catalogues have used it since Phase 6; this screen
// and `ContactsPage` were the last customer-facing surfaces without it.
// `GET /api/records/export` serves both formats through
// `routes/enquiry-export.ts`, and the download carries this screen's own filter and
// sort — never its cursor, because an export is one document over one filter.
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { SgdsButton, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { REFUSAL_REMEDY, type RefusalKind } from '@prs2/contracts'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { usePager } from '../lib/usePager'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { Money } from '../components/Money'
import { formatIsoDate, formatInstant } from '../lib/format'
import { targetValue } from '../lib/sgds-dom'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'
import { BillStatusBadge, StateBadge } from '../components/StatusBadge'
import { plainCode } from '../lib/plain-words'

const ALL = 'ALL'

/**
 * What `?outcome=` may seed the dropdown with.
 *
 * ⚠ An unrecognised value falls back to {@link ALL} rather than being passed
 * through. The route would answer 400 to `?outcome=MAYBE`, and a screen whose
 * first render guarantees an error toast from a URL somebody mistyped is worse
 * than one that opens unfiltered — the dropdown then shows what is actually
 * being asked for, which is the honest state either way.
 */
function initialOutcome(raw: string | null): string {
  return raw === 'ACCEPTED' || raw === 'REJECTED' ? raw : ALL
}

/** Mirrors `RECORD_SORT_COLUMNS` in `services/transfer/src/inbound-file-repository.ts` exactly. */
/**
 * ★★★ WAVE 16 — three keys were missing here, and the table rendered their
 * headers as plain `<th>`s while every other one sorted.
 *
 * This repository's standing rule is that EVERY data table header sorts, and its
 * own note says the ones that get missed are the ones the builder judged
 * unimportant. These three were not judged unimportant — they are nullable, and
 * this list walks a server-side keyset cursor that could not express "continue
 * from among the rows with no value". `RECORD_SORT_COLUMNS` now can, so they sort.
 *
 * ⚠ SERVER-side, like the other three. The list is capped and cursor-paged, so a
 * client sort would reorder one page while looking like it reordered the enquiry.
 */
type RecordSortKey =
  | 'receivedAt'
  | 'outcome'
  | 'counterpartyCode'
  | 'sourceReference'
  | 'amount'
  | 'billStatus'

const COLUMN_LABELS: Readonly<Record<RecordSortKey, string>> = {
  receivedAt: 'Received',
  outcome: 'Outcome',
  counterpartyCode: 'Counterparty',
  sourceReference: 'Your reference',
  amount: 'Amount',
  billStatus: 'Bill status',
}

export function RecordsPage() {
  const { sort, onSort, interacted } = useSort<RecordSortKey>()
  const pager = usePager()

  /**
   * ★★★ WAVE 27 — this screen now opens on the narrowing a LINK asked for.
   *
   * ⚠ **This exists because a link that does not filter is worse than no link.**
   * The dashboard's interface-alerts tile links each refusal reason to the records
   * behind it. Until this change every filter here lived in local state alone, so
   * `/records?outcome=REJECTED&reasonKind=UNKNOWN_OFFICER` would have rendered the
   * unfiltered enquiry under a heading that said nothing was wrong — the officer
   * reads "15 unknown officer" on the dashboard, clicks it, and lands on 2,125
   * records with no way to know the narrowing was dropped. That is the same class
   * as a refusal shipped without its way out.
   *
   * ⚠ **The URL seeds the INITIAL state and does not own it.** Changing the
   * dropdown afterwards must not need a navigation, and a `useState` initialiser
   * runs once — so a later link to the same screen with different parameters is
   * handled by `useEffect` below rather than by re-reading on every render, which
   * would fight the dropdown for control on every keystroke.
   */
  const [searchParams, setSearchParams] = useSearchParams()
  const [outcomeFilter, setOutcomeFilter] = useState<string>(() => initialOutcome(searchParams.get('outcome')))
  const [reasonFilter, setReasonFilter] = useState<string | null>(() => searchParams.get('reasonKind'))

  // A real server-side filter (unlike BillsListPage's status filter, which
  // narrows only the loaded page): `outcome` is a genuine `?outcome=` the
  // route accepts, so changing it restarts paging — same discipline sort
  // changes already follow.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => pager.reset(), [sort.column, sort.dir, outcomeFilter, reasonFilter])

  /**
   * A NEW link to this screen re-seeds both filters — arriving from the dashboard
   * twice, for two different reasons, must show two different things.
   *
   * ⚠ Keyed on the raw query string rather than on the parsed values, so
   * navigating from `?reasonKind=A` to `?reasonKind=B` re-runs while a dropdown
   * change (which does not touch the URL) does not.
   */
  const rawQuery = searchParams.toString()
  useEffect(() => {
    setOutcomeFilter(initialOutcome(searchParams.get('outcome')))
    setReasonFilter(searchParams.get('reasonKind'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawQuery])

  const records = useApi(
    () =>
      api.records({
        ...sortParams(sort),
        ...(outcomeFilter !== ALL && { outcome: outcomeFilter as 'ACCEPTED' | 'REJECTED' }),
        ...(reasonFilter !== null && { reasonKind: reasonFilter }),
        ...(pager.cursor !== undefined && { cursor: pager.cursor }),
      }),
    [sort.column, sort.dir, outcomeFilter, reasonFilter, pager.cursor],
  )

  /**
   * ★★ WAVE 9 — ONE statement of what the download is being asked for, shared by
   * both format buttons and built from the SAME state the table's own fetch uses.
   *
   * ⚠ Deliberately without the cursor. P2C1 §12.52 asks for the query to be
   * downloadable, and the query is the filter — not the page of it the officer
   * happens to be looking at.
   */
  const exportState = {
    ...sortParams(sort),
    ...(outcomeFilter !== ALL && { outcome: outcomeFilter }),
    ...(reasonFilter !== null && { reasonKind: reasonFilter }),
  }

  const rows = records.data?.records ?? []
  const total = records.data?.total ?? 0
  const nextCursor = records.data?.nextCursor ?? null

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">Records</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            What happened to a delivery, record by record: accepted or rejected, with the reason and the way out
            for a rejection, and the split, provenance and any later correction for an acceptance. Filtered
            to your access: this list can only ever show deliveries your own grants cover.
          </p>
        </div>
        {/*
          ★★★ WAVE 27 — ONE GROUP, owner-directed. Print sat alone in the corner
          while three near-identical outline buttons spread across the width below
          the paragraph: four controls answering one question — "what do I want to
          do with this list?" — arranged as if they answered four.

          ⚠ THE DOWNLOADS ARE STILL PLAIN NAVIGATIONS, not fetches: the route
          answers with an `attachment` Content-Disposition, so the browser
          downloads and the officer stays here. They carry this screen's FILTER and
          SORT and not its cursor, so the file is the enquiry rather than the page,
          and the document states its own row bound and REFUSES past it instead of
          truncating — a partial enquiry presented as complete is what would let an
          absent rejection read as good news.

          ⚠ AND THE EMAIL CARRIES THE SAME `exportState`, so the attachment and the
          two downloads describe one population. They diverge the moment somebody
          passes a different object to one of them.
        */}
        <ExportToolbar
          onPrint={() => window.print()}
          onDownload={(format) => {
            window.location.href = api.recordsExportUrl(format, exportState)
          }}
          email={<EmailReportButton path="/api/records/export" query={exportState as Record<string, string>} />}
        />
      </div>

      <div className="pt-card pt-controlbar pt-no-print">
        <SgdsSelect
          label="Outcome"
          hintText="Re-queries the server over your whole scope, not only this page."
          value={outcomeFilter}
          onSgdsChange={(e) => setOutcomeFilter(targetValue(e))}
        >
          <SgdsSelectOption value={ALL}>All outcomes</SgdsSelectOption>
          <SgdsSelectOption value="ACCEPTED">Accepted</SgdsSelectOption>
          <SgdsSelectOption value="REJECTED">Rejected</SgdsSelectOption>
        </SgdsSelect>
        {/*
          ⚠ A narrowing that came from a LINK says so ON SCREEN and can be undone
          here. An invisible filter is how a reader concludes their scope is empty:
          the count, the table and the download would all be narrowed while nothing
          on the page admitted it.
        */}
        {reasonFilter !== null && (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm">
              Narrowed to refusal reason <strong className="pt-tabular">{plainCode(reasonFilter)}</strong>
            </span>
            <SgdsButton
              variant="outline"
              onClick={() => {
                setReasonFilter(null)
                const next = new URLSearchParams(searchParams)
                next.delete('reasonKind')
                setSearchParams(next, { replace: true })
              }}
            >
              Clear reason
            </SgdsButton>
          </div>
        )}
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={records.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {records.error !== null ? (
        <ErrorCard title="Could not load records" message={records.error} onRetry={records.reload} />
      ) : records.initialLoading ? (
        <LoadingCard label="Loading records" rows={6} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No records in scope"
          message={
            reasonFilter !== null || outcomeFilter !== ALL
              ? `No record in your scope matches this narrowing. Widen it above to see the rest.`
              : "Nothing has happened yet for the counterparties your active identity's grants cover."
          }
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={records.loading}>
            <table className="pt-table">
              <caption className="sr-only">
                Records received from counterparties in your scope
                {outcomeFilter !== ALL ? `, narrowed to outcome ${plainCode(outcomeFilter)}` : ''}
                {reasonFilter !== null ? `, narrowed to refusal reason ${plainCode(reasonFilter)}` : ''}, one page at a time
              </caption>
              <thead>
                <tr>
                  <SortableTh label="Received" sortKey="receivedAt" sort={sort} onSort={onSort} />
                  <SortableTh label="Counterparty" sortKey="counterpartyCode" sort={sort} onSort={onSort} />
                  <SortableTh label={COLUMN_LABELS.sourceReference} sortKey="sourceReference" sort={sort} onSort={onSort} />
                  <SortableTh label="Outcome" sortKey="outcome" sort={sort} onSort={onSort} />
                  <SortableTh label={COLUMN_LABELS.billStatus} sortKey="billStatus" sort={sort} onSort={onSort} />
                  {/*
                    ⚠ Sorting Amount DESC puts the largest bills first and the rows
                    with no amount LAST — `NULLS LAST` in both directions, server
                    side. PostgreSQL's own default would open a descending sort
                    with every record that has no amount, burying exactly the rows
                    the reader clicked the header to see.
                  */}
                  <SortableTh label={COLUMN_LABELS.amount} sortKey="amount" sort={sort} onSort={onSort} align="right" />
                  {/*
                    ⚠ NOT sortable, and the reason is arithmetic rather than taste.
                    The cell renders `row.adjustments.length`, and that array is
                    TRUNCATED at `MAX_ADJUSTMENTS_PER_RECORD` by the route — so its
                    length is not the record's correction count once a record has more
                    than five. Sorting on it would order by min(count, 5), putting every
                    heavily-corrected record in one indistinguishable block at the top
                    while claiming to rank them. It is also `null` for a record whose
                    bill is outside the caller's scope, which is a third meaning again.
                  */}
                  <th
                    scope="col"
                    data-unsortable="the count shown is a list truncated at MAX_ADJUSTMENTS_PER_RECORD, so sorting it would order by min(count, 5) while claiming to rank the most-corrected records"
                  >
                    Corrections
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const remedy = row.reasonKind !== null ? REFUSAL_REMEDY[row.reasonKind as RefusalKind] : undefined
                  return (
                    <tr key={row.id}>
                      <td className="pt-tabular" title={formatInstant(row.receivedAt)}>
                        {formatIsoDate(row.receivedDate)}
                      </td>
                      <td>{row.counterpartyCode}</td>
                      <td className="pt-mono text-sm">{row.sourceReference ?? 'None'}</td>
                      <td>
                        <div className="flex flex-col gap-1 items-start">
                          <StateBadge value={row.outcome === 'ACCEPTED' ? 'ACCEPTED' : 'REJECTED'} />
                          {remedy !== undefined && (
                            <span className="text-xs text-[var(--sgds-body-color-subtle)] max-w-[36ch]">{remedy}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {row.effectReference !== null ? (
                          <Link to={`/bills/${row.effectReference}`} className="pt-ref-link">
                            {row.billStatus === null ? row.effectReference : <BillStatusBadge status={row.billStatus} />}
                          </Link>
                        ) : (
                          <span className="text-[var(--sgds-color-subtle)]">None</span>
                        )}
                      </td>
                      <td className="pt-num">
                        <Money cents={row.amountCents} placeholder={row.outcome === 'REJECTED' ? 'Not applicable' : 'Not in your bill scope'} />
                      </td>
                      <td>
                        {row.adjustments === null ? (
                          <span className="text-[var(--sgds-color-subtle)]">Not applicable</span>
                        ) : row.adjustments.length === 0 ? (
                          <span className="text-[var(--sgds-color-subtle)]">None</span>
                        ) : (
                          <span title={row.adjustments.map((a) => `#${a.seq} ${a.indicator} ${a.deltaAmountCents}c`).join(', ')}>
                            {row.adjustments.length} correction{row.adjustments.length === 1 ? '' : 's'}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            <p className="text-sm text-[var(--sgds-color-subtle)]">
              Showing {rows.length} of {total} record{total === 1 ? '' : 's'} in your scope.{' '}
              {sort.column === null
                ? 'The server default order.'
                : `Sorted on the server by ${COLUMN_LABELS[sort.column]} (${sort.dir === 'asc' ? 'ascending' : 'descending'}) over your whole scope.`}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <SgdsButton
                variant="outline"
                size="sm"
                type="button"
                disabled={!pager.canGoPrevious || records.loading}
                onClick={pager.goPrevious}
              >
                Previous
              </SgdsButton>
              <SgdsButton
                variant="outline"
                size="sm"
                type="button"
                disabled={nextCursor === null || records.loading}
                onClick={() => {
                  if (nextCursor !== null) pager.goNext(nextCursor)
                }}
              >
                Next
              </SgdsButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
